/**
 * First-touch UTM attribution.
 *
 * Captures the five standard UTM parameters from the URL the very first
 * time the visitor arrives in this session, then persists them in
 * sessionStorage so they survive client-side navigation and modal opens.
 *
 * Subsequent visits to deeper pages (where the URL no longer carries
 * UTMs) still see the original first-touch values, which is what we
 * want for waitlist attribution.
 *
 * Notes:
 *  - We intentionally do NOT overwrite existing values. First touch wins.
 *  - Empty strings are stored for missing params so the form can render
 *    consistent hidden inputs.
 *  - All access is wrapped in try/catch in case sessionStorage is blocked
 *    (private browsing, embedded contexts).
 */

export const UTM_KEYS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
];

const STORAGE_KEY = "tp-utm-first-touch";

const safeGet = () => {
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (err) {
        console.debug("utm: sessionStorage read blocked", err);
        return null;
    }
};

const safeSet = (data) => {
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (err) {
        console.debug("utm: sessionStorage write blocked", err);
    }
};

const emptyUtms = () =>
    UTM_KEYS.reduce((acc, k) => {
        acc[k] = "";
        return acc;
    }, {});

/**
 * Read UTMs from the current URL and persist them on first touch.
 * Safe to call multiple times. Safe to call before mount / on every page.
 */
export const captureUtmsOnFirstTouch = () => {
    if (typeof window === "undefined") return emptyUtms();

    const existing = safeGet();
    if (existing) return existing;

    const params = new URLSearchParams(window.location.search);
    const captured = emptyUtms();
    let anyPresent = false;
    UTM_KEYS.forEach((k) => {
        const v = params.get(k);
        if (v) {
            captured[k] = v;
            anyPresent = true;
        }
    });

    // Only persist if at least one UTM was present. Otherwise keep storage
    // empty so a later landing with UTMs can still claim first touch.
    if (anyPresent) safeSet(captured);
    return captured;
};

/**
 * Return the stored first-touch UTMs, or read fresh from the URL if
 * nothing has been stored yet. Always returns all five keys (empty
 * string when missing) so callers can spread it into form fields or
 * analytics payloads without null checks.
 */
export const getUtms = () => {
    if (typeof window === "undefined") return emptyUtms();
    const existing = safeGet();
    if (existing) return { ...emptyUtms(), ...existing };
    return captureUtmsOnFirstTouch();
};
